/**
 * Help Modal Component
 *
 * Explains what Hence is and how to use it.
 */

import { useState } from "react";

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  planUrl?: string;
}

function CopyableCommand({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-2 bg-base-200 rounded-lg p-2 font-mono text-xs">
      <code className="flex-1 overflow-x-auto">{command}</code>
      <button
        className="btn btn-ghost btn-xs shrink-0"
        onClick={handleCopy}
        title="Copy to clipboard"
      >
        {copied ? (
          <svg className="w-4 h-4 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    </div>
  );
}

export default function HelpModal({ isOpen, onClose, planUrl }: HelpModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-base-100 rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-base-300">
          <h2 className="text-lg font-semibold">Welcome to Hence</h2>
          <button
            className="btn btn-ghost btn-sm btn-circle"
            onClick={onClose}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-auto space-y-4 text-sm">
          <section>
            <h3 className="font-semibold text-base mb-2">What is Hence?</h3>
            <p className="text-base-content/70">
              Hence is a collaborative task planning tool powered by defeasible reasoning.
              It automatically determines which tasks are ready, blocked, or complete based
              on dependencies and rules you define.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-base mb-2">Task Statuses</h3>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="badge badge-success badge-sm">done</span>
                <span className="text-base-content/70">Task is complete</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="badge badge-secondary badge-sm">in progress</span>
                <span className="text-base-content/70">Someone is working on it</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="badge badge-primary badge-sm">ready</span>
                <span className="text-base-content/70">All dependencies met, can be started</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="badge badge-warning badge-sm">blocked</span>
                <span className="text-base-content/70">Cannot proceed due to a blocker</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="badge badge-ghost badge-sm">backlog</span>
                <span className="text-base-content/70">Waiting for dependencies</span>
              </div>
            </div>
          </section>

          <section>
            <h3 className="font-semibold text-base mb-2">Views</h3>
            <div className="space-y-1.5 text-base-content/70">
              <p><strong>Board:</strong> Kanban-style columns by status</p>
              <p><strong>DAG:</strong> Dependency graph showing task relationships</p>
              <p><strong>Tree:</strong> Hierarchical view of task dependencies</p>
            </div>
          </section>

          <section>
            <h3 className="font-semibold text-base mb-2">Proof Tree</h3>
            <p className="text-base-content/70">
              Click any task to see its proof tree - how Hence derived its status
              using defeasible logic. <span className="text-success">+d</span> means
              defeasibly proven, <span className="text-warning">-d</span> means defeated.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-base mb-2">Keyboard Shortcuts</h3>
            <div className="space-y-1 font-mono text-xs">
              <div className="flex justify-between">
                <span className="text-base-content/70">Search tasks</span>
                <kbd className="kbd kbd-sm">⌘K</kbd>
              </div>
              <div className="flex justify-between">
                <span className="text-base-content/70">Command palette</span>
                <kbd className="kbd kbd-sm">⌘⇧K</kbd>
              </div>
            </div>
          </section>

          <section>
            <h3 className="font-semibold text-base mb-2">CLI Tool</h3>
            <p className="text-base-content/70 mb-2">
              Manage plans from your terminal with the Hence CLI:
            </p>
            <div className="space-y-2">
              <CopyableCommand command="curl -fsSL hence.run/install | bash" />
              {planUrl && (
                <>
                  <CopyableCommand command={`hence next ${planUrl}`} />
                  <p className="text-base-content/50 text-xs">
                    Shows available tasks from this board
                  </p>
                </>
              )}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-base-300 bg-base-200/50">
          <button className="btn btn-primary btn-sm w-full" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
